import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import catalogProducts from "@/data/catalogProducts";
import { isCustomPricedProduct } from "@/lib/pricing";

const StoreContext = createContext(null);

const CART_KEY = "mnfst_cart";
const WISH_KEY = "mnfst_wishlist";
const catalogById = new Map(catalogProducts.map((product) => [product.id, product]));

const formatVoucherAmount = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return null;
    }
    return Math.round(numeric * 100) / 100;
};

const readLS = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
};

export const StoreProvider = ({ children }) => {
    const [cart, setCart] = useState(() => readLS(CART_KEY, []));
    const [wishlist, setWishlist] = useState(() => readLS(WISH_KEY, []));
    const [cartOpen, setCartOpen] = useState(false);

    useEffect(() => {
        setCart((prev) => {
            let changed = false;

            const nextCart = prev.flatMap((item) => {
                const product = catalogById.get(item.product_id);
                if (!product) {
                    changed = true;
                    return [];
                }

                const normalizedSize = product.sizes?.includes(item.size)
                    ? item.size
                    : product.sizes?.[0] ?? item.size;
                const normalizedColor = product.colors?.includes(item.color)
                    ? item.color
                    : product.colors?.[0] ?? item.color;
                const customAmount = isCustomPricedProduct(product)
                    ? formatVoucherAmount(item.customAmount ?? item.custom_amount ?? item.price ?? product.price)
                    : null;
                const resolvedPrice = isCustomPricedProduct(product) && customAmount !== null
                    ? customAmount
                    : product.price;
                const nextKey = isCustomPricedProduct(product)
                    ? `${product.id}-${normalizedSize}-${normalizedColor}-${resolvedPrice.toFixed(2)}`
                    : `${product.id}-${normalizedSize}-${normalizedColor}`;
                const nextItem = {
                    ...item,
                    key: nextKey,
                    product_id: product.id,
                    name: product.name,
                    price: resolvedPrice,
                    image: product.images?.[0],
                    size: normalizedSize,
                    color: normalizedColor,
                    customAmount,
                };

                if (JSON.stringify(nextItem) !== JSON.stringify(item)) {
                    changed = true;
                }

                return [nextItem];
            });

            if (!changed) {
                return prev;
            }

            const mergedCart = nextCart.reduce((acc, item) => {
                const existing = acc.find((entry) => entry.key === item.key);
                if (existing) {
                    existing.quantity += item.quantity;
                    return acc;
                }

                acc.push(item);
                return acc;
            }, []);

            return mergedCart;
        });
    }, []);

    useEffect(() => {
        localStorage.setItem(CART_KEY, JSON.stringify(cart));
    }, [cart]);

    useEffect(() => {
        localStorage.setItem(WISH_KEY, JSON.stringify(wishlist));
    }, [wishlist]);

    const addToCart = useCallback((product, size, color, qty = 1, options = {}) => {
        setCart((prev) => {
            const customAmount = isCustomPricedProduct(product)
                ? formatVoucherAmount(options.customAmount ?? product.price)
                : null;
            const resolvedPrice = isCustomPricedProduct(product) && customAmount !== null
                ? customAmount
                : product.price;
            const key = isCustomPricedProduct(product)
                ? `${product.id}-${size}-${color}-${resolvedPrice.toFixed(2)}`
                : `${product.id}-${size}-${color}`;
            const existing = prev.find((i) => i.key === key);
            if (existing) {
                return prev.map((i) =>
                    i.key === key ? { ...i, quantity: i.quantity + qty } : i
                );
            }
            return [
                ...prev,
                {
                    key,
                    product_id: product.id,
                    name: product.name,
                    price: resolvedPrice,
                    image: product.images?.[0],
                    size,
                    color,
                    quantity: qty,
                    customAmount,
                },
            ];
        });
        setCartOpen(true);
    }, []);

    const updateQty = useCallback((key, qty) => {
        setCart((prev) =>
            prev
                .map((i) => (i.key === key ? { ...i, quantity: qty } : i))
                .filter((i) => i.quantity > 0)
        );
    }, []);

    const removeFromCart = useCallback((key) => {
        setCart((prev) => prev.filter((i) => i.key !== key));
    }, []);

    const clearCart = useCallback(() => setCart([]), []);

    const toggleWishlist = useCallback((product) => {
        setWishlist((prev) => {
            const exists = prev.find((p) => p.id === product.id);
            if (exists) return prev.filter((p) => p.id !== product.id);
            return [
                ...prev,
                {
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    image: product.images?.[0],
                    category: product.category,
                },
            ];
        });
    }, []);

    const inWishlist = useCallback(
        (id) => wishlist.some((p) => p.id === id),
        [wishlist]
    );

    const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
    const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);

    return (
        <StoreContext.Provider
            value={{
                cart,
                cartCount,
                cartTotal,
                cartOpen,
                setCartOpen,
                addToCart,
                updateQty,
                removeFromCart,
                clearCart,
                wishlist,
                toggleWishlist,
                inWishlist,
            }}
        >
            {children}
        </StoreContext.Provider>
    );
};

export const useStore = () => {
    const ctx = useContext(StoreContext);
    if (!ctx) throw new Error("useStore must be inside StoreProvider");
    return ctx;
};
